import {ChatBskyConvoDefs, ChatBskyGroupDefs} from '@atproto/api'
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {DM_SERVICE_HEADERS} from '#/lib/constants'
import {useAgent} from '#/state/session'

const DEFAULT_LIMIT = 10

export const RQKEY_ROOT = 'convo-request-list'
export const RQKEY = (limit: number = DEFAULT_LIMIT) => [RQKEY_ROOT, limit]

type RQPageParam = string | undefined

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConvoRequestItem = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConvoRequestListQueryData = InfiniteData<any>

export function useListConvoRequests({
  enabled = true,
  limit = DEFAULT_LIMIT,
}: {
  enabled?: boolean
  limit?: number
} = {}) {
  const agent = useAgent()

  return useInfiniteQuery({
    enabled,
    queryKey: RQKEY(limit),
    queryFn: async ({pageParam}) => {
      const {data} = await agent.chat.bsky.convo.listConvoRequests(
        {limit, cursor: pageParam},
        {headers: DM_SERVICE_HEADERS},
      )
      return data
    },
    initialPageParam: undefined as RQPageParam,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getNextPageParam: (lastPage: any) => lastPage.cursor,
  })
}

export function optimisticUpdate(
  chatId: string,
  old: ConvoRequestListQueryData | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateFn: (convo: any) => any,
): ConvoRequestListQueryData | undefined {
  if (!old) return old

  return {
    ...old,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pages: old.pages.map((page: any) => ({
      ...page,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      requests: page.requests.map((item: any): ConvoRequestItem => {
        if (ChatBskyConvoDefs.isConvoView(item) && item.id === chatId) {
          return {
            ...updateFn(item),
            $type: 'chat.bsky.convo.defs#convoView',
          }
        }
        return item
      }),
    })),
  }
}

export function optimisticDelete(
  chatId: string,
  old: ConvoRequestListQueryData | undefined,
) {
  if (!old) return old

  return {
    ...old,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pages: old.pages.map((page: any) => ({
      ...page,
      requests: page.requests.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) =>
          !ChatBskyConvoDefs.isConvoView(item) || item.id !== chatId,
      ),
    })),
  }
}

export function markAllRead(
  old: ConvoRequestListQueryData | undefined,
): ConvoRequestListQueryData | undefined {
  if (!old) return old

  return {
    ...old,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pages: old.pages.map((page: any) => ({
      ...page,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      requests: page.requests.map((item: any): ConvoRequestItem => {
        if (ChatBskyConvoDefs.isConvoView(item)) {
          return {
            ...item,
            $type: 'chat.bsky.convo.defs#convoView',
            unreadCount: 0,
          }
        }
        return item
      }),
    })),
  }
}

export function optimisticDeleteJoinRequest(
  convoId: string,
  old: ConvoRequestListQueryData | undefined,
) {
  if (!old) return old

  return {
    ...old,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pages: old.pages.map((page: any) => ({
      ...page,
      requests: page.requests.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any) =>
          !ChatBskyGroupDefs.isJoinRequestConvoView(item) ||
          item.convoId !== convoId,
      ),
    })),
  }
}

export function* findAllProfilesInQueryData(
  queryClient: QueryClient,
  did: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queryDatas = queryClient.getQueriesData<InfiniteData<any>>({
    queryKey: [RQKEY_ROOT],
  })
  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData?.pages) continue

    for (const page of queryData.pages) {
      for (const item of page.requests) {
        if (ChatBskyConvoDefs.isConvoView(item)) {
          for (const member of item.members) {
            if (member.did === did) {
              yield member
            }
          }
        } else if (ChatBskyGroupDefs.isJoinRequestConvoView(item)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const joinReqItem = item as any
          if (joinReqItem.owner?.did === did) {
            yield joinReqItem.owner
          }
        }
      }
    }
  }
}
